import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, rawApi, apiError, isAuthError, RETURN_TO_KEY } from "../lib/api";
import { loadToken, setToken, clearToken, isNative, getCachedToken } from "../lib/nativeAuth";
import { initLocalFirst, teardownLocalFirst, getLocalMe, bootstrapLocalDb, setSyncListener } from "../lib/local";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  // After the outbox flushes on reconnect, the server's authoritative records are written
  // back to local SQLite — invalidate queries so the Work card etc. refresh automatically
  // (no force-close/relaunch required).
  useEffect(() => {
    setSyncListener(() => { queryClient.invalidateQueries(); });
    return () => setSyncListener(null);
  }, [queryClient]);

  const check = useCallback(async () => {
    try {
      await loadToken(); // native: hydrate bearer from secure storage (no-op on web)
      if (isNative()) { try { await bootstrapLocalDb(); } catch { /* offline-safe */ } }
      const { data } = await api.get("/auth/me");
      if (data && data.id) { setUser(data); }
      else if (isNative() && getCachedToken()) { setUser((await getLocalMe()) || false); }
      else { setUser(false); }
    } catch (e) {
      // Server rejected the token (expired/invalid): force re-authentication, never trust it.
      if (isAuthError(e)) {
        const hadToken = isNative() && !!getCachedToken();
        await clearToken();
        teardownLocalFirst();
        setUser(false);
        if (hadToken) toast.error("Your session has expired. Please sign in again.");
      }
      // Offline / backend unreachable: keep an already-authenticated native session alive
      // using the locally cached identity + Keychain token (never weakens auth).
      else if (isNative() && getCachedToken()) { setUser((await getLocalMe()) || false); }
      else { setUser(false); }
    } finally {
      setReady(true);
    }
  }, []);

  // Native local-first: once authenticated, init the encrypted DB, seed once, flush outbox.
  useEffect(() => {
    if (user && user.id) initLocalFirst(user, rawApi).catch(() => {});
  }, [user]);

  useEffect(() => { check(); }, [check]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) return { ok: false, mfaRequired: true, mfaToken: data.mfa_token };
      await setToken(data.token); // native only; web ignores (cookie set by server)
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const register = async (email, password, name, originUrl, acceptTerms) => {
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name, origin_url: originUrl, accept_terms: acceptTerms,
      });
      await setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const verifyMfa = async (mfaToken, code) => {
    try {
      const { data } = await api.post("/auth/login/mfa", { mfa_token: mfaToken, code });
      await setToken(data.token);
      setUser(data.user);
      return { ok: true, usedBackup: data.used_backup_code };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    await clearToken(); // native: wipe Keychain/Keystore credential
    teardownLocalFirst(); // stop routing local-first for this session (data retained on device)
    setUser(false);
  };

  // Called when the server rejects the session mid-use (401). Optional returnTo is honoured by Login.
  const expireSession = useCallback(async (returnTo) => {
    if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);
    await clearToken();
    teardownLocalFirst();
    setUser(false);
    toast.error("Your session has expired. Please sign in again.");
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, register, verifyMfa, logout, expireSession, refreshUser: check }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
